import {
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
  DateRangeChipsArray,
  GridFilterDateInput,
  type DateRangeFilter,
  getThisMonthDateRange,
} from 'components';
import {
  DetailDrawer,
  CreateActivityBoardItemDialog,
  UpdateActivityBoardItemDialog,
  DeleteActivityBoardItemDialog,
} from './components';
import {
  Box,
  Alert,
  Button,
  Divider,
  useTheme,
  TextField,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
} from '@mui/material';
import {
  GridColDef,
  useGridApiRef,
  GridColTypeDef,
  GRID_DATE_COL_DEF,
  isAutogeneratedRow,
  GridActionsCellItem,
  getGridDateOperators,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { Book } from 'pages/dashboard';
import { Project } from 'library/types';
import { useSnackbar } from 'notistack';
import { isLastDayOfMonth } from './utils';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { gray } from 'providers/theme/themePrimitives';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { ActivityBoardItem, ActivityType, User } from 'library/types';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { STATUS_TYPE_ENUM, STORAGE_KEY_ENUM, TABLE_SCHEMA_PAGE_NAME_ENUM } from 'enums';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { Authentication, renderAutoCompleteLocaleText, renderDataGridLocaleText, renderFormattedDate } from 'utils';

const dateColumnType: GridColTypeDef<Date, string> = {
  ...GRID_DATE_COL_DEF,
  resizable: false,
  filterOperators: getGridDateOperators(false).map((item) => ({
    ...item,
    InputComponent: GridFilterDateInput,
    InputComponentProps: { showTime: false },
  })),
};

const DEFAULT_DATE_CHIP_KEY = 'thisMonth';

const ActivityManaging = () => {
  // other hooks
  const intl = useIntl();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const pageName = TABLE_SCHEMA_PAGE_NAME_ENUM.ACTIVITY_MANAGING;

  // redux hooks
  const dispatch = useAppDispatch();
  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);
  const permissionCodes = useAppSelector(selectPermissionCodes);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState<Book[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [openDetail, setOpenDetail] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [detailRow, setDetailRow] = useState<ActivityBoardItem | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Project | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeFilter>(getThisMonthDateRange);
  const [selectedDateChipKey, setSelectedDateChipKey] = useState(DEFAULT_DATE_CHIP_KEY);
  const [activityBoardItems, setActivityBoardItems] = useState<ActivityBoardItem[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const showMonthEndOvertimeReminder = isLastDayOfMonth();

  const selectedRow = useMemo(
    () => activityBoardItems.find((row) => row.id === rowSelectionModel[0]) ?? null,
    [activityBoardItems, rowSelectionModel]
  );

  const filteredActivityTypes = useMemo(() => {
    const me = users.find((user) => user.nameSurname === Authentication.getLoggedUser());

    if (!me?.roleId) return activityTypes;

    return activityTypes.filter((at) => at.roles.some((role) => role.id === me.roleId));
  }, [users, activityTypes]);

  const openRowDetail = useCallback((row: ActivityBoardItem) => {
    setDetailRow(row);
    setOpenDetail(true);
  }, []);

  const closeRowDetail = useCallback(() => {
    setOpenDetail(false);
    setDetailRow(null);
  }, []);

  // CRUD dialog handlers
  const handleClickOpenCreateDialog = () => setOpenCreateDialog(true);
  const handleCloseCreateDialog = () => setOpenCreateDialog(false);

  const handleClickOpenUpdateDialog = () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    const isAdmin = permissionCodes.includes('update-activity-board-item-admin');
    const selectedRow = activityBoardItems.find((row) => row.id === rowSelectionModel[0]);
    const activityType = activityTypes.find((at) => at.id === selectedRow?.activityTypeId);
    const isPermissionActivity = activityType?.isPermissionActivity === true;

    if (isPermissionActivity && !isAdmin) {
      enqueueSnackbar(
        <FormattedMessage
          id="activityManaging.isPermissionActivityUpdateAdminRequired"
          defaultMessage="İzin aktivitesini güncellemek için admin yetkisine sahip olmalısınız!"
        />,
        {
          variant: 'warning',
        }
      );
      return;
    }

    if (!isAdmin) {
      const meId = users.find((user) => user.nameSurname === Authentication.getLoggedUser())?.id;

      if (selectedRow?.assignedUserId !== meId) {
        enqueueSnackbar(
          <FormattedMessage
            id="activityManaging.notOwnerUpdate"
            defaultMessage="Yalnızca tarafınıza ait aktiviteleri güncelleyebilirsiniz!"
          />,
          {
            variant: 'warning',
          }
        );
        return;
      }
    }

    setOpenUpdateDialog(true);
  };
  const handleCloseUpdateDialog = () => setOpenUpdateDialog(false);

  const handleClickOpenDeleteDialog = () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    const isAdmin = permissionCodes.includes('delete-activity-board-item-admin');
    const selectedRow = activityBoardItems.find((row) => row.id === rowSelectionModel[0]);
    const activityType = activityTypes.find((at) => at.id === selectedRow?.activityTypeId);
    const isPermissionActivity = activityType?.isPermissionActivity === true;

    if (isPermissionActivity && !isAdmin) {
      enqueueSnackbar(
        <FormattedMessage
          id="activityManaging.isPermissionActivityDeleteAdminRequired"
          defaultMessage="İzin aktivitesini silmek için admin yetkisine sahip olmalısınız!"
        />,
        {
          variant: 'warning',
        }
      );
      return;
    }

    if (!isAdmin) {
      const meId = users.find((user) => user.nameSurname === Authentication.getLoggedUser())?.id;

      if (selectedRow?.assignedUserId !== meId) {
        enqueueSnackbar(
          <FormattedMessage
            id="activityManaging.notOwnerDelete"
            defaultMessage="Yalnızca tarafınıza ait aktiviteleri silebilirsiniz!"
          />,
          {
            variant: 'warning',
          }
        );
        return;
      }
    }

    setOpenDeleteDialog(true);
  };
  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

  const columns: GridColDef<ActivityBoardItem>[] = useMemo(
    () => [
      {
        field: 'actions',
        type: 'actions',
        headerName: intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' }),
        width: 120,
        getActions: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return [];
          }

          return [
            <GridActionsCellItem
              key="detail"
              icon={<VisibilityOutlinedIcon sx={{ color: 'primary.main', opacity: 0.85 }} />}
              label={intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' })}
              onClick={() => openRowDetail(params.row as ActivityBoardItem)}
              showInMenu={false}
            />,
          ];
        },
      },
      {
        field: 'bookId',
        headerName: intl.formatMessage({ id: 'activityManaging.book', defaultMessage: 'Proje' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => books.filter((book): book is Book => !!book && book.id != null).map((book) => book.id),
        getOptionLabel: (value) => books.find((book) => String(book.id) === String(value))?.companyName ?? '',
        valueFormatter: (value) => books.find((book) => String(book.id) === String(value))?.companyName ?? '',
        minWidth: 200,
      },
      {
        field: 'projectId',
        headerName: intl.formatMessage({ id: 'activityManaging.product', defaultMessage: 'Ürün' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () =>
          products
            .filter((project): project is Project => !!project && project.id != null)
            .map((project) => project.id),
        getOptionLabel: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        valueFormatter: (value) => products.find((project) => String(project.id) === String(value))?.title ?? '',
        minWidth: 200,
      },
      {
        field: 'assignedUserId',
        headerName: intl.formatMessage({ id: 'activityManaging.assignedUser', defaultMessage: 'Atanan Kullanıcı' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => users.find((user) => user.id === value)?.nameSurname || '',
        minWidth: 200,
      },
      {
        field: 'activityTypeId',
        headerName: intl.formatMessage({ id: 'activityManaging.activityType', defaultMessage: 'Aktivite Türü' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => activityTypes.find((at) => at.id === value)?.title || '',
        minWidth: 200,
      },
      {
        field: 'effortTrackNo',
        headerName: intl.formatMessage({ id: 'activityManaging.effortTrackNo', defaultMessage: 'Efor Takip No' }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 250,
      },
      {
        field: 'description',
        headerName: intl.formatMessage({ id: 'activityManaging.description', defaultMessage: 'Açıklama' }),
        minWidth: 250,
      },
      {
        field: 'startDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'activityManaging.startDate', defaultMessage: 'Başlangıç Tarihi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
        minWidth: 150,
      },
      {
        field: 'endDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'activityManaging.endDate', defaultMessage: 'Bitiş Tarihi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value, true) : ''),
        minWidth: 150,
      },
      {
        field: 'estimatedHours',
        headerName: intl.formatMessage({
          id: 'activityManaging.estimatedHours',
          defaultMessage: 'Harcanan Süre (saat)',
        }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        minWidth: 150,
      },
      {
        field: 'isOvertimeWork',
        headerName: intl.formatMessage({ id: 'activityManaging.isOvertimeWork', defaultMessage: 'Fazla Mesai' }),
        headerAlign: 'center',
        type: 'boolean',
        minWidth: 130,
      },
    ],
    [intl, books, products, users, activityTypes, openRowDetail]
  );

  // async helper functions
  const getUsers = async () => {
    try {
      const response = await apiService.getUsers();

      if (response.isSuccess) {
        const formattedData = response.data
          .filter((user) => user.status === STATUS_TYPE_ENUM.ACTIVE)
          .sort((a, b) => (a.nameSurname || '').localeCompare(b.nameSurname || ''));
        setUsers(formattedData);
      }
    } catch {}
  };

  const getBooks = async () => {
    try {
      const response = await apiService.getBooks();

      if (response.isSuccess) {
        const sortedData = [...response.data].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
        setBooks(sortedData);
      }
    } catch {}
  };

  const getActivityTypes = async () => {
    try {
      const response = await apiService.getActivityTypes();

      if (response.isSuccess) setActivityTypes(response.data);
    } catch {}
  };

  const handleDateChipChange = useCallback((key: string, range: DateRangeFilter) => {
    setSelectedDateChipKey(key);
    setDateRange(range);
  }, []);

  const getActivityBoardItems = useCallback(async () => {
    try {
      setLoading(true);

      const response = await apiService.getActivityBoardItems({
        bookId: selectedBook?.id || null,
        projectId: selectedProduct?.id || null,
        userId: selectedUser?.id || null,
        startDate: dateRange.startedDate,
        endDate: dateRange.endDate,
      });

      if (response.isSuccess) {
        const sortedData = [...response.data].sort(
          (a, b) => new Date(b.startDate ?? 0).getTime() - new Date(a.startDate ?? 0).getTime()
        );
        setActivityBoardItems(sortedData);
        setRowSelectionModel([]);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [selectedBook, selectedProduct, selectedUser, dateRange]);

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (
      !permissionCodes.includes('list-activity-board-item') &&
      !permissionCodes.includes('list-activity-board-item-admin')
    ) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    getUsers();
    getBooks();
    getActivityTypes();
  }, []);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    getActivityBoardItems();
  }, [getActivityBoardItems]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-activity-board-item']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenCreateDialog}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="activityManaging.createActivityBoardItem" defaultMessage="Aktivite Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['update-activity-board-item', 'update-activity-board-item-admin']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenUpdateDialog}
              fullWidth
              size="medium"
              variant="contained"
              color="info"
              endIcon={<EditIcon />}>
              <FormattedMessage id="activityManaging.updateActivityBoardItem" defaultMessage="Aktivite Güncelle" />
            </Button>
          </Grid>
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['delete-activity-board-item', 'delete-activity-board-item-admin']}>
          <Grid size={2.5}>
            <Button
              onClick={handleClickOpenDeleteDialog}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="activityManaging.deleteActivityBoardItem" defaultMessage="Aktivite Sil" />
            </Button>
          </Grid>
        </PermissionHandler>

        {showMonthEndOvertimeReminder && (
          <Grid size={{ xs: 12 }}>
            <Alert severity="warning" variant="outlined" sx={{ width: '100%', alignItems: 'center', mt: 1 }}>
              <FormattedMessage
                id="activityManaging.monthEndOvertimeReminder"
                defaultMessage="Bugün ayın son günü. İçinde bulunduğumuz aya ait Fazla Mesai aktivitelerini ay kapanmadan girmeniz gerekir."
              />
            </Alert>
          </Grid>
        )}
      </Grid>

      <DateRangeChipsArray selectedChipKey={selectedDateChipKey} onChipChange={handleDateChipChange} />

      <DataGridBox>
        <ZebraDataGrid<ActivityBoardItem>
          pagination
          checkboxSelection
          rowSelectionPropagation={{
            parents: false,
            descendants: false,
          }}
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel((prev) => {
              const prevStr = JSON.stringify(prev);
              const nextStr = JSON.stringify(newRowSelectionModel);

              return prevStr === nextStr ? prev : newRowSelectionModel;
            });
          }}
          rowSelectionModel={rowSelectionModel}
          columns={columns}
          initialState={{
            aggregation: {
              model: {
                estimatedHours: 'sum',
              },
            },
          }}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={activityBoardItems}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              exportPremiumExcel: true,
              showTemplates: true,
              apiRef,
              pageName,
              singleFilter: true,
              singleFilterComponent: (
                <Box display="flex" justifyContent="center" alignItems="center" mb={1}>
                  <Box>
                    <FormattedMessage id="activityManaging.book" defaultMessage="Proje" />
                    &#58;&nbsp;
                  </Box>
                  <Autocomplete
                    size="small"
                    sx={{ width: '10vw' }}
                    options={books}
                    value={selectedBook}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Book | null) => setSelectedBook(newValue)}
                    getOptionLabel={(option: Book) => option.companyName ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <Divider orientation="vertical" sx={{ marginX: 1, border: 0 }} />
                  <Box>
                    <FormattedMessage id="activityManaging.product" defaultMessage="Ürün" />
                    &#58;&nbsp;
                  </Box>
                  <Autocomplete
                    size="small"
                    sx={{ width: '10vw' }}
                    options={products}
                    value={selectedProduct}
                    {...renderAutoCompleteLocaleText()}
                    onChange={(_, newValue: Project | null) => setSelectedProduct(newValue)}
                    getOptionLabel={(option: Project) => option.title ?? ''}
                    slotProps={{
                      paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        sx={{
                          '& .MuiButtonBase-root': {
                            border: 'none',
                            backgroundColor: 'inherit',
                            width: '2.25rem',
                            height: '2.25rem',
                            padding: '0.25rem',
                            [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <PermissionHandler requiredPermissionCodes={['list-activity-board-item-admin']}>
                    <Divider orientation="vertical" sx={{ marginX: 1, border: 0 }} />
                    <Box>
                      <FormattedMessage id="activityManaging.assignedUser" defaultMessage="Atanan Kullanıcı" />
                      &#58;&nbsp;
                    </Box>
                    <Autocomplete
                      size="small"
                      sx={{ width: '10vw' }}
                      options={users}
                      value={selectedUser}
                      {...renderAutoCompleteLocaleText()}
                      onChange={(_, newValue: User | null) => setSelectedUser(newValue)}
                      getOptionLabel={(option: User) => option.nameSurname ?? ''}
                      slotProps={{
                        paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          sx={{
                            '& .MuiButtonBase-root': {
                              border: 'none',
                              backgroundColor: 'inherit',
                              width: '2.25rem',
                              height: '2.25rem',
                              padding: '0.25rem',
                              [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                              },
                            },
                          }}
                        />
                      )}
                    />
                  </PermissionHandler>
                </Box>
              ),
            },
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
              },
            },
          }}
          disableRowGrouping
          apiRef={apiRef}
        />
      </DataGridBox>

      <CreateActivityBoardItemDialog
        open={openCreateDialog}
        handleClose={handleCloseCreateDialog}
        books={books}
        products={products}
        activityTypes={filteredActivityTypes}
        users={users}
        refresh={getActivityBoardItems}
      />
      <UpdateActivityBoardItemDialog
        open={openUpdateDialog}
        handleClose={handleCloseUpdateDialog}
        selectedRow={selectedRow}
        books={books}
        products={products}
        activityTypes={filteredActivityTypes}
        users={users}
        refresh={getActivityBoardItems}
      />
      <DeleteActivityBoardItemDialog
        open={openDeleteDialog}
        activityBoardItemId={selectedRow?.id ?? null}
        handleClose={handleCloseDeleteDialog}
        refresh={getActivityBoardItems}
        setRowSelectionModel={setRowSelectionModel}
      />
      <DetailDrawer
        open={openDetail}
        close={closeRowDetail}
        detailRow={detailRow}
        users={users}
        books={books}
        products={products}
        activityTypes={activityTypes}
      />
    </>
  );
};

export default ActivityManaging;
